import { createEffect, createSignal, type JSX, Show, untrack } from 'solid-js';
import { t } from '../i18n';
import { type ApiToken, tokenRenameCall } from '../lib/tokens';
import { CopyAsCall } from './CopyAsCall';
import { Field } from './Field';
import { Modal } from './Modal';
import { Button, buttonClass } from './ui/Button';

// Relabel-a-PAT dialog (#2828). Presenter — `createTokenRename` owns the PATCH
// and the re-read.
//
// It moves the LABEL and nothing else, which is what the copy has to say: the
// scopes, the expiry and the credential itself are untouched, so a reader is
// never left wondering whether renaming a token they cannot identify might also
// have changed what it can do. A REVOKED row is renameable for the same reason
// the API allows it — labelling a credential you just killed is how the roster
// stays readable.

export type RenameTokenModalProps = {
  /** The token being relabelled, or null when the dialog is shut. */
  readonly token: ApiToken | null;
  readonly pending: boolean;
  /** The API's reason for refusing, shown inline. */
  readonly error?: string | null;
  readonly onRename: (name: string) => void;
  readonly onClose: () => void;
};

export function RenameTokenModal(props: RenameTokenModalProps): JSX.Element {
  const [draft, setDraft] = createSignal('');

  // The dialog stays MOUNTED between tokens (so `useClosing` can play its exit),
  // so the field is seeded per open rather than at mount. `untrack` keeps a list
  // refetch mid-edit from clobbering what the reader is typing.
  createEffect(() => {
    if (props.token !== null) setDraft(untrack(() => props.token?.name ?? ''));
  });

  // Trimmed, because that is what the API stores (`ApiTokenNameSchema` trims):
  // the snippet and the submit describe one string, and a name of only spaces is
  // refused here rather than at the edge.
  const label = (): string => draft().trim();

  const submit = (e: Event): void => {
    e.preventDefault();
    if (label().length === 0 || props.pending) return;
    props.onRename(label());
  };

  return (
    <Modal
      open={props.token !== null}
      title={t('account_tokens.rename_title')}
      desc={t('account_tokens.rename_desc')}
      onClose={props.onClose}
    >
      <form class="rename-token__form" onSubmit={submit}>
        <Field
          label={t('account_tokens.form_name')}
          id="rename-token-name"
          desc={t('account_tokens.form_name_desc')}
        >
          {(a11y) => (
            <input
              {...a11y}
              class="input"
              type="text"
              maxLength={60}
              value={draft()}
              onInput={(e) => setDraft(e.currentTarget.value)}
              placeholder={t('account_tokens.form_name_placeholder')}
              required
            />
          )}
        </Field>

        {props.error ? (
          <p class="field__error" role="alert">
            {props.error}
          </p>
        ) : null}

        {/* Built from the DRAFT, and withheld while it would be refused — the
            same rule the mint's disclosure follows. */}
        <Show when={props.token !== null && label().length > 0}>
          {(_) => (
            <CopyAsCall
              calls={[
                {
                  label: t('account_tokens.rename'),
                  call: tokenRenameCall(props.token?.id ?? '', label()),
                },
              ]}
            />
          )}
        </Show>

        <div class="dialog__footer">
          <Button variant="outline" onClick={props.onClose}>
            {t('account_tokens.cancel')}
          </Button>
          <button
            type="submit"
            class={buttonClass()}
            disabled={label().length === 0 || props.pending}
          >
            {t(props.pending ? 'account_tokens.renaming' : 'account_tokens.rename_submit')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
